import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AdminLiveCourseForm from './AdminLiveCourseForm';
import AdminLiveCourseRegistrationsModal from './AdminLiveCourseRegistrationsModal';

const AdminLiveCourseManager = () => {
    const [courses, setCourses] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // State for modals
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingCourseId, setEditingCourseId] = useState(null);
    const [viewingRegistrationsId, setViewingRegistrationsId] = useState(null);

    const fetchCourses = useCallback(async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('app_token');
            const response = await axios.get('/api/admin/live-courses', {
                headers: { Authorization: `Bearer ${token}` }
            });
            // Ensure the response data is an array before setting state
            if (Array.isArray(response.data)) {
                setCourses(response.data);
            } else {
                // If data is not an array, log an error and keep courses as an empty array
                console.error("API did not return an array for live courses:", response.data);
                setCourses([]);
            }
            setError(null);
        } catch (err) {
            setError("Failed to load live courses.");
            setCourses([]); // Ensure courses is an empty array on error
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCourses();
    }, [fetchCourses]);

    const handleOpenForm = (courseId = null) => {
        setEditingCourseId(courseId);
        setIsFormOpen(true);
    };

    const handleCloseForm = () => {
        setEditingCourseId(null);
        setIsFormOpen(false);
    };

    const handleSave = async (formData) => {
        const token = localStorage.getItem('app_token');
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const url = editingCourseId ? `/api/admin/live-courses/${editingCourseId}` : '/api/admin/live-courses';
        const method = editingCourseId ? 'put' : 'post';

        try {
            await axios[method](url, formData, config);
            fetchCourses();
            handleCloseForm();
        } catch (err) {
            alert('Failed to save live course.');
            console.error(err);
        }
    };

    const handleDelete = async (courseId) => {
        if (window.confirm('Are you sure you want to permanently delete this live course?')) {
            try {
                const token = localStorage.getItem('app_token');
                await axios.delete(`/api/admin/live-courses/${courseId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                fetchCourses();
            } catch (err) {
                alert('Failed to delete live course.');
            }
        }
    };

    const handleStatusToggle = async (course) => {
        try {
            const token = localStorage.getItem('app_token');
            await axios.put(`/api/admin/live-courses/${course.id}/status`,
                { isActive: !course.is_active },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            fetchCourses();
        } catch (err) {
            alert('Failed to update status.');
        }
    };

    if (isLoading) return <p>Loading live courses...</p>;
    if (error) return <p className="error-message">{error}</p>;

    return (
        <div className="admin-content-manager">
            {isFormOpen && (
                <AdminLiveCourseForm
                    courseId={editingCourseId}
                    onSave={handleSave}
                    onCancel={handleCloseForm}
                />
            )}
            {viewingRegistrationsId && (
                <AdminLiveCourseRegistrationsModal
                    courseId={viewingRegistrationsId}
                    onCancel={() => setViewingRegistrationsId(null)}
                />
            )}

            <div className="admin-content-header">
                <h3 className="admin-section-title">Manage Live Courses</h3>
                <button onClick={() => handleOpenForm()} className="btn-primary">
                    Add New Live Course
                </button>
            </div>

            <div className="admin-table-container">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Title</th>
                            <th>Start Time</th>
                            <th>Price</th>
                            <th>Status</th>
                            <th>Active</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {courses.map(course => (
                            <tr key={course.id}>
                                <td>{course.id}</td>
                                <td>{course.title}</td>
                                <td>{new Date(course.start_datetime).toLocaleString()}</td>
                                <td>₹{course.price}</td>
                                <td><span className={`role-badge`}>{course.status}</span></td>
                                <td>
                                    <div className="status-cell">
                                        <label className="toggle-switch">
                                            <input type="checkbox" checked={course.is_active} onChange={() => handleStatusToggle(course)} />
                                            <span className="toggle-slider"></span>
                                        </label>
                                    </div>
                                </td>
                                <td className="admin-table-actions">
                                    <button onClick={() => setViewingRegistrationsId(course.id)} className="action-btn" title="View Registrations">
                                        <span className="material-icons">people</span>
                                    </button>
                                    <button onClick={() => handleOpenForm(course.id)} className="action-btn edit" title="Edit">
                                        <span className="material-icons">edit</span>
                                    </button>
                                    <button onClick={() => handleDelete(course.id)} className="action-btn delete" title="Delete">
                                        <span className="material-icons">delete_forever</span>
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AdminLiveCourseManager;